import React, { createContext, useState, useContext, useEffect } from 'react';
import { getApiUrl } from '../config/api';

const ProjectContext = createContext(null);

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};

export const ProjectProvider = ({ children }) => {
  const [projects, setProjects] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [switchingProject, setSwitchingProject] = useState(false);
  const [userPlan, setUserPlan] = useState('basic');
  const [projectLimit, setProjectLimit] = useState(null);

  const getAuthToken = () => {
    return localStorage.getItem('scanmaster_token');
  };

  useEffect(() => {
    loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProjects = async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await fetch(getApiUrl('projects'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const projectsList = data.projects || [];
        
        // Transform projects to match expected format
        const formattedProjects = projectsList.map(p => ({
          id: p.id,
          name: p.name,
          url: p.url,
          createdAt: p.created_at
        }));

        setProjects(formattedProjects);
        setUserPlan(data.userPlan || 'basic');
        // Effective project limit for this user (from backend)
        setProjectLimit(
          typeof data.projectLimit === 'number' ? data.projectLimit : null
        );

        // Set current project from localStorage or first project
        const storedCurrentProject = localStorage.getItem('scanmaster_current_project');
        if (storedCurrentProject) {
          try {
            const parsed = JSON.parse(storedCurrentProject);
            const found = formattedProjects.find(p => p.id === parsed.id);
            if (found) {
              setCurrentProject(found);
            } else if (formattedProjects.length > 0) {
              setCurrentProject(formattedProjects[0]);
            }
          } catch (e) {
            if (formattedProjects.length > 0) {
              setCurrentProject(formattedProjects[0]);
            }
          }
        } else if (formattedProjects.length > 0) {
          setCurrentProject(formattedProjects[0]);
        }
      } else {
        const errorData = await response.json().catch(() => ({ error: response.statusText }));
        console.error('Failed to load projects:', errorData.error || response.statusText);
        console.error('Response status:', response.status);
        
        // If it's a 401, the token might be invalid - try to refresh
        if (response.status === 401) {
          console.warn('Authentication failed, token may be expired');
        }
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const createProject = async (project) => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(getApiUrl('projects'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: project.name,
          url: project.url
        })
      });

      const data = await response.json();

      if (response.ok) {
        const newProject = {
          id: data.project.id,
          name: data.project.name,
          url: data.project.url,
          createdAt: data.project.created_at
        };

        const newProjects = [...projects, newProject];
        setProjects(newProjects);
        setCurrentProject(newProject);
        localStorage.setItem('scanmaster_current_project', JSON.stringify(newProject));
        return newProject;
      } else {
        const errorMessage = data.error || 'Failed to create project';
        console.error('Project creation failed:', errorMessage);
        console.error('Response status:', response.status);
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Error creating project:', error);
      throw error;
    }
  };

  const updateProject = (projectId, updates) => {
    // For now, just update local state
    // TODO: Add API endpoint for updating projects if needed
    const updatedProjects = projects.map((p) =>
      p.id === projectId ? { ...p, ...updates } : p
    );
    setProjects(updatedProjects);

    if (currentProject?.id === projectId) {
      const updatedCurrent = { ...currentProject, ...updates };
      setCurrentProject(updatedCurrent);
      localStorage.setItem('scanmaster_current_project', JSON.stringify(updatedCurrent));
    }
  };

  const deleteProject = async (projectId) => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(getApiUrl(`projects/${projectId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const filteredProjects = projects.filter((p) => p.id !== projectId);
        setProjects(filteredProjects);

        if (currentProject?.id === projectId) {
          const newCurrent = filteredProjects.length > 0 ? filteredProjects[0] : null;
          setCurrentProject(newCurrent);
          if (newCurrent) {
            localStorage.setItem('scanmaster_current_project', JSON.stringify(newCurrent));
          } else {
            localStorage.removeItem('scanmaster_current_project');
          }
        }
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete project');
      }
    } catch (error) {
      console.error('Error deleting project:', error);
      throw error;
    }
  };

  const selectProject = (project) => {
    // Only show loading if switching to a different project
    if (currentProject && currentProject.id !== project.id) {
      setSwitchingProject(true);
      // Set a timeout to hide loading after project switch completes
      setTimeout(() => {
        setSwitchingProject(false);
      }, 500);
    }
    setCurrentProject(project);
    localStorage.setItem('scanmaster_current_project', JSON.stringify(project));
  };

  return (
    <ProjectContext.Provider
      value={{
        projects,
        currentProject,
        createProject,
        updateProject,
        deleteProject,
        selectProject,
        loading,
        switchingProject,
        userPlan,
        projectLimit,
        refreshProjects: loadProjects,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
};

